import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { Pill } from 'ui-components/components/pill';

import { toFixedNumber } from '../../../services/number-formatting-service';
import { WeightUnit } from '../../../enums/weight-unit';
import { UnitConversionService } from '../../../services/unit-conversion-service';
import Global from '../../../store/global-selectors';
import { getShipFromFulfillmentSummary } from '../../carrier-pickup-api';

import { messages } from './messages';
import style from './style.module.less';

const FulfillmentSummary = ({ fulfillmentSummary = null }) => {
  const sellerDefaultWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
  const fulfillments = fulfillmentSummary.totalFulfillments || 0;
  const packages = fulfillmentSummary.totalPackages || 0;
  const weight = fulfillmentSummary.totalWeight || 0;
  const weightUnit = fulfillmentSummary.weightUnit || WeightUnit.Pound;

  const convertedWeight = useMemo(() => {
    if (weightUnit === WeightUnit.Pound && sellerDefaultWeightUnit === WeightUnit.Grams) {
      return {
        weightValue: toFixedNumber(UnitConversionService.convertValue(weight, WeightUnit.Pounds, WeightUnit.Kilograms), 3),
        weightUnitMessageDescriptor: messages.weightUnitKilograms,
      };
    }
    return {
      weightValue: toFixedNumber(weight, 1),
      weightUnitMessageDescriptor: messages.weightUnitPounds,
    };
  }, [sellerDefaultWeightUnit, weight, weightUnit]);

  return (
    <>
      <div className={style['pill-row']}>
        <Pill label={`${fulfillments}`} />
        <span className={style['pill-title']}>
          <FormattedMessage {...messages.shipmentsTitle} />
        </span>
      </div>
      <div className={style['pill-row']}>
        <Pill label={`${packages}`} />
        <span className={style['pill-title']}>
          <FormattedMessage {...messages.packagesTitle} />
        </span>
      </div>
      <div className={style['pill-row']}>
        <Pill label={`${convertedWeight.weightValue}`} />
        <span className={style['pill-title']}>
          <FormattedMessage {...convertedWeight.weightUnitMessageDescriptor} values={{ weight: convertedWeight.weightValue }} />
        </span>
      </div>
    </>
  );
};

const SummaryPlaceholder = () => (
  <div className={style['no-summary']}>
    <FormattedMessage {...messages.noSummary} />
  </div>
);

/**
 * FulfillmentPreview component that fetches and displays fulfillment summary
 * @param {object} props
 * @param {string} [props.pickupDate] - Pickup date for filtering fulfillments
 * @param {string} [props.pickupServiceCode] - Pickup service code for filtering
 * @param {number} [props.shipFromId] - ID of the ship from location
 * @param {string} [props.sellerLabelProviderId] - ID of the seller label provider
 * @param {string} [props.timeZoneIana] - IANA time zone identifier
 * @param {boolean} [props.shouldUseDateRangeFromToday] - Whether to use a date range of {warehouse current date} - {pickupDate}, or just the 24 hour range of pickupDate
 * @returns {React.ReactElement} The FulfillmentPreview component
 */
export const FulfillmentPreview = ({
  pickupDate = '',
  pickupServiceCode = null,
  shipFromId = null,
  sellerLabelProviderId = '',
  timeZoneIana = '',
  shouldUseDateRangeFromToday = false,
}) => {
  const [fulfillmentSummary, setFulfillmentSummary] = useState(null);

  const shouldFetch = shipFromId && sellerLabelProviderId && pickupDate;

  useEffect(() => {
    if (!shouldFetch) {
      return;
    }

    getShipFromFulfillmentSummary(
      shipFromId,
      sellerLabelProviderId,
      pickupDate,
      pickupServiceCode,
      timeZoneIana,
      shouldUseDateRangeFromToday,
    )
      .then((data) => {
        setFulfillmentSummary(data);
      })
      .catch(() => {
        setFulfillmentSummary(null);
      });
  }, [shouldFetch, shipFromId, sellerLabelProviderId, pickupDate, pickupServiceCode, timeZoneIana, shouldUseDateRangeFromToday]);

  return (
    <div className={style['right-preview']}>
      <div className={style['preview-title']}>
        <FormattedMessage {...messages.openShipments} />
      </div>
      {fulfillmentSummary ? <FulfillmentSummary fulfillmentSummary={fulfillmentSummary} /> : <SummaryPlaceholder />}
    </div>
  );
};
